import {createFilteredContactFetcher} from "../contact-list/filtered-contact-fetcher";
import {createContactFetcher, type FetcherType} from "../contact-list/contact-fetcher";
import {createContactFilterGroup} from "../contact-list/contact-filter-group";
import type {ContactsResponse} from "../contact-list/api-types";
import {createContactList} from "../contact-list/contact-list";
import {debounceFlowByEventCycles} from "../contact-list/flow-batch-by-event-cycles";
import { onDomReady } from "../../on-dom-ready";

const positionLoadingIndicator = (root: HTMLElement, loadingIndicator: HTMLElement | null) => {
  if (!loadingIndicator) {
    return
  }

  const minDistance = 20 // px

  const absoluteScreenMiddle = document.documentElement.scrollTop + window.innerHeight / 2

  const rootTop = root.offsetTop
  const rootHeight = root.clientHeight
  const loadingIndicatorHeight = parseFloat(getComputedStyle(loadingIndicator).height)

  if (rootHeight < minDistance * 2 + loadingIndicatorHeight) {
    loadingIndicator.style.top = (rootHeight / 2 - loadingIndicatorHeight / 2) + "px"
    return
  }

  if (absoluteScreenMiddle - loadingIndicatorHeight / 2 < rootTop + minDistance) {
    loadingIndicator.style.top = minDistance + "px"
    return
  }

  if (absoluteScreenMiddle + loadingIndicatorHeight / 2 > rootTop + rootHeight - minDistance) {
    loadingIndicator.style.top = (rootHeight - minDistance - loadingIndicatorHeight) + "px"
    return
  }

  loadingIndicator.style.top = (absoluteScreenMiddle - rootTop - loadingIndicatorHeight / 2) + "px"
}

/**
 * Backbone of the simple contact list with dynamic filters plugins,
 * namely "Contacts by market" and "Product specific Contacts".
 *
 * Both only consist of a dynamic filter group and a single contact list -
 * when the filter group fetched new contacts, the list updates, ... .
 *
 * The more interesting part is the different static additions to the
 * contact request, like the product for the "Product specific Contacts".
 * These are completely left to the server side rendering due to the fact
 * that the fetcher always constructs its requests based on the current state
 * of the underlying dom form.
 */
const initializeSimpleFilterableContactList = (root: HTMLElement) => {
  const firstResponse = (JSON.parse(root.dataset['firstResponsePayload'] ?? '{}') as ContactsResponse)
  const fetcherType: FetcherType = (root.dataset['fetcherType'] as FetcherType) ?? undefined
  const loadingIndicator = root.querySelector<HTMLElement>(".loading-indicator")!!
  const emptyOption = root.dataset["emptyOption"]

  document.addEventListener("scroll", () => {
    positionLoadingIndicator(root, loadingIndicator.querySelector<HTMLElement>('img'))
  })

  const filteredFetch = createFilteredContactFetcher(
    createContactFetcher(
      root.querySelector<HTMLFormElement>('form.filterable-contact-list__fetch-form')!!,
      fetcherType
    ),
    createContactFilterGroup(
      firstResponse.filterInputFields ?? [],
      root.querySelector<HTMLElement>('.filterable-contact-list__filter-container')!!,
      loadingIndicator,
      emptyOption
    ),
    loadingIndicator
  );

  const list = createContactList(
    root.querySelector('.filterable-contact-list__contacts-container')!!
  )
  list.update(firstResponse.contacts ?? [], firstResponse.message)

  filteredFetch.updates
    .with(debounceFlowByEventCycles)
    .each(async ({response: update}) => {
      list.update(update.contacts ?? [], update.message)
    })
}

onDomReady(() => {
  for (const rootElement of document.getElementsByClassName('filterable-contact-list--simple')) {
    if (!(rootElement instanceof HTMLElement)) {
      continue;
    }
    try {
      initializeSimpleFilterableContactList(rootElement)
    } catch (e) {
      console.error("Failed to initialize simple filterable contact list. Hiding element cause I feel ashamed. ", e)
      rootElement.hidden = true
    }
  }
});

function InitLeftAndRightButtons() {
  const leftButton = document.querySelector('.filterable-contact-list__action-button-left') as HTMLElement | null;
  const rightButton = document.querySelector('.filterable-contact-list__action-button-right') as HTMLElement | null;
  const containerScroll = document.querySelector('.filterable-contact-list__contacts-container') as HTMLElement | null;

  if (!leftButton || !rightButton || !containerScroll) {
    console.error('Could not find the left or right button or the container');
    return;
  }

  window.addEventListener('resize', () => {
    if (self.innerWidth > 1024) {
      leftButton.classList.add('hidden');
      rightButton.classList.add('hidden');
    } else {
      leftButton.classList.remove('hidden');
      rightButton.classList.remove('hidden');
    }
  })

  const updateScrollAmount = () => {
    // Clean up previous event listeners
    leftButton.onclick = rightButton.onclick = null;

    const items = containerScroll.querySelectorAll('.filterable-contact-list__contact__wrapper');
    const itemsCount = items.length;
    const middle = Math.floor(itemsCount / 2);

    if (items && items.length === 0) {
      leftButton.style.display = rightButton.style.display = 'none';
      return;
    }

    items.forEach((item, index) => {
      item.classList.remove('filterable-contact-list__contact__wrapper-start', 'filterable-contact-list__contact__wrapper-center', 'filterable-contact-list__contact__wrapper-end');
      if (itemsCount > 4) {
        if (index < middle) item.classList.add('filterable-contact-list__contact__wrapper-start');
        else if (index === middle) item.classList.add('filterable-contact-list__contact__wrapper-center');
        else item.classList.add('filterable-contact-list__contact__wrapper-end');
        if (window.innerWidth > 1024) {
          leftButton.classList.add('hidden');
          rightButton.classList.add('hidden');
        }
      } else {
        item.classList.add('filterable-contact-list__contact__wrapper-center');
        leftButton.classList.add('hidden');
        rightButton.classList.add('hidden');
      }
    });

    const firstItem = items[0] as HTMLElement;
    const itemWidth = firstItem ? firstItem.getBoundingClientRect().width : 0;
    const gapValue = parseFloat(getComputedStyle(containerScroll).gap) || 0;
    const scrollAmount = itemWidth + gapValue;

    const scrollByAmount = (amount: number) => containerScroll.scrollBy({ top: 0, left: amount, behavior: 'smooth' });

    leftButton.onclick = () => scrollByAmount(-scrollAmount);
    rightButton.onclick = () => scrollByAmount(scrollAmount);

    // Initial scroll to center
    scrollByAmount(scrollAmount);
    scrollByAmount(-scrollAmount);
  };

  new MutationObserver(updateScrollAmount).observe(containerScroll, { childList: true, subtree: true });
  updateScrollAmount();
}

onDomReady(InitLeftAndRightButtons);
